import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import lockIcon from '../../../assets/images/lock.png';
import redLockIcon from '../../../assets/images/redLock.png';
import greenLockIcon from '../../../assets/images/greenLock.png';
import emailSendIcon from '../../../assets/images/email-send.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkEmailRequest,
  verifyOtpRequest,
  setOtpSuccess,
  savePreferrenceRequest,
  fetchPreferencesRequest,
  fetchPrefandNotifsRequest
} from '../../../redux/slices/userSlice';
import { useNavigate } from 'react-router-dom';

const CustomButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: '#1460AA',
  color: 'white',
  border: 'none',
  padding: '.75rem 2rem',
  cursor: 'pointer',
  // fontFamily: "'Open Sans', sans-serif",
  fontSize: '1rem',
  borderRadius: '25px',
  fontWeight: 700,
  textTransform: 'none',
  transition:
    'background-color ease-in-out .15s, border-color ease-in-out .15s, box-shadow ease-in-out .15s',
  '&:hover': {
    backgroundColor: '#125a9a'
  },
  '&:disabled': {
    backgroundColor: '#ccc'
  }
}));

const OtpInput = styled(TextField)<{ isError: boolean }>(
  ({ theme, isError }) => ({
    margin: theme.spacing(1),
    '& input': {
      textAlign: 'center',
      fontSize: '.8rem',
      padding: '.25rem',
      fontWeight: 600,
      borderRadius: '4px',
      width: '1.5rem',
      height: '1.48rem',
      color: isError ? '#851109' : '#000'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: '3px solid #717173'
      },
      '&:hover fieldset': {
        border: '3px solid #5a5a5a' // Darker shade of #717173 for hover
      },
      '&.Mui-focused fieldset': {
        border: '3px solid #8a8a8a' // Lighter shade of #717173 for focused
      },
      '&.Mui-error fieldset': {
        border: '3px solid #851109' // Red color for error
      }
    }
  })
);

interface OtpOptionsProps {
  step: string;
  setStep: (step: string) => void;
  header: string;
  setSnackbarMessage: (message: string) => void; // Optional
  setSnackbarOpen: (open: boolean) => void; // Optional
  setSnackbarSeverity: (severity: string) => void; // Optional
  snackbarMessage: string; // Optional
  snackbarOpen: boolean; // Optional
  handleClose?: () => void; // Optional
  email: string;
}

const OtpOptions: React.FC<OtpOptionsProps> = ({
  step,
  header,
  email,
  setStep,
  setSnackbarMessage,
  setSnackbarOpen,
  setSnackbarSeverity,
  snackbarMessage,
  snackbarOpen,
  handleClose
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [otp, setOtp] = useState(Array(5).fill(''));
  const [language, setLanguage] = useState('en');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const { t, i18n } = useTranslation();
  const inputRefs = useRef<any>([]);
  const [preferences, setPreferences] = useState({
    NT001: false,
    NT002: false,
    NT003: false,
    PT001: false,
    PT002: false
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isError, setIsError] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile devices
  const isLargeMob = useMediaQuery('(min-width: 600px) and (max-width: 767px)');
  const isTablet = useMediaQuery('max-width: 1024px');

  const data: any = t('OtpOptions', { returnObjects: true, defaultValue: {} });

  const otpError = useSelector((state: any) => state.user.error);
  const otpSuccess = useSelector((state: any) => state.user.otp);
  const success = useSelector((state: any) => state.user.success);
  const notificationArray = useSelector(
    (state: any) => state.user.notifications
  );
  const preferenceArray = useSelector((state: any) => state.user.preferences);
  const savedPreferences = useSelector(
    (state: any) => state.user.savedPreferences
  );

  useEffect(() => {
    setIsButtonDisabled(otp.some((digit) => digit === ''));
  }, [otp]);

  const menuOptions = [
    { value: 'en', label: data.language.english },
    { value: 'fr', label: data.language.french }
  ];

  const handleFocus = (event) => {
    event.target.setSelectionRange(0, 1);
  };

  const handleSubmit = () => {
    const enteredOtp = otp.join('');
    dispatch(verifyOtpRequest({ email, otp: enteredOtp }));
  };

  useEffect(() => {
    if (step === 'managePreferences') {
      dispatch(fetchPrefandNotifsRequest());
      dispatch(fetchPreferencesRequest(email));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, email]);

  useEffect(() => {
    if (success && otpSuccess) {
      setIsError(false);
      setOtpSuccess(false);
      setStep('verificationSuccess');
    } else if (otpError) {
      setIsError(true);
      setIsButtonDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpError, otpSuccess, success]);

  useEffect(() => {
    if (step === 'managePreferences') {
      //check if all preferences and notifications are unchecked
      const allUnchecked = !Object.values(preferences).some((value) => value);
      if (hasInteracted && allUnchecked) {
        setSnackbarMessage(data.snackbar.warning);
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      } else {
        setSnackbarOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, hasInteracted, step]);

  useEffect(() => {
    if (snackbarOpen) {
      const snackbarElement = document.querySelector('.MuiSnackbar-root');
      if (snackbarElement) {
        snackbarElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [snackbarOpen]);

  useEffect(() => {
    if (savedPreferences) {
      setPreferences({
        PT001: savedPreferences.prefs?.includes('PT001') || false,
        NT001: savedPreferences.notifs?.includes('NT001') || false,
        PT002: savedPreferences.prefs?.includes('PT002') || false,
        NT003: savedPreferences.notifs?.includes('NT003') || false,
        NT002: savedPreferences.notifs?.includes('NT002') || false
      });
      setLanguage(savedPreferences.language_preference || 'en');
      const hasCheckedPreferences =
        Object.values(savedPreferences.prefs || {}).some((value) => value) ||
        Object.values(savedPreferences.notifs || {}).some((value) => value);
      setHasInteracted(hasCheckedPreferences);
    } else {
      setPreferences({
        NT001: false,
        NT002: false,
        NT003: false,
        PT001: false,
        PT002: false
      });
      setLanguage('en');
      setHasInteracted(false);
      // setIsFetchingPreferences(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPreferences]);

  const otpDialogRef = useRef<HTMLDivElement>(null);

  const headingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only run when dialog is launched for OTP entry
    if (step === 'enterCode') {
      //Focus dialog container
      if (otpDialogRef.current) {
        otpDialogRef.current.focus();
      }
      // Delay focus to allow screen reader to announce dialog title/instructions
      const timeout = setTimeout(() => {
        const firstOtp = document.getElementById('otp-digit-1');
        if (firstOtp) {
          firstOtp.focus();
        }
      }, 350); // 350ms for extra safety

      return () => clearTimeout(timeout);
    }
  }, [step]);

  const handlePreferenceChange = (event) => {
    const { name, checked } = event.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSavePreferences = (e: any) => {
    e.preventDefault();
    if (snackbarOpen) {
      setSnackbarOpen(false);
    }
    if (!agreeTerms) {
      setSnackbarMessage(data.snackbar.errorTerms);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else if (
      Object.values(preferences).every((value) => !value) &&
      !hasInteracted
    ) {
      setSnackbarMessage(data.snackbar.notSelected);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      const payload = {
        newsletter_user_email: email,
        newsletter_user_opted_prefs: Object.keys(preferences).filter(
          (key) => preferences[key] && key.startsWith('PT')
        ),
        newsletter_user_opted_notifs: Object.keys(preferences).filter(
          (key) => preferences[key] && key.startsWith('NT')
        ),
        language_preference: language || 'en'
      };
      dispatch(savePreferrenceRequest(payload));
      setSnackbarMessage(data.snackbar.successPreferences);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setStep('preferencesSaved');
      setHasInteracted(true);
    }
  };
  const handleChange = (index) => (event) => {
    const { value } = event.target;
    const newOtp = [...otp];
    newOtp[index] = value.trim().slice(-1);
    setOtp(newOtp);

    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus();
    }

    // Reset error state when user starts entering new OTP
    if (isError) {
      setIsError(false);
    }
  };

  const handlePaste = (event) => {
    const paste = event.clipboardData.getData('text').trim();
    const newOtp = [...otp];
    paste.split('').forEach((char, index) => {
      if (index < newOtp.length) {
        newOtp[index] = char;
      }
    });
    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length, newOtp.length) - 1].focus();

    // Reset error state when user pastes new OTP
    if (isError) {
      setIsError(false);
    }
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
      setTimeout(() => {
        inputRefs.current[index - 1].setSelectionRange(1, 1);
      }, 0);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
      setTimeout(() => {
        inputRefs.current[index - 1].setSelectionRange(1, 1);
      }, 0);
    } else if (
      event.key === 'ArrowRight' &&
      index < inputRefs.current.length - 1
    ) {
      inputRefs.current[index + 1].focus();
      setTimeout(() => {
        inputRefs.current[index + 1].setSelectionRange(1, 1);
      }, 0);
    }
  };

  useEffect(() => {
    if (step === 'managePreferences' && headingRef.current) {
      headingRef.current.focus();
    }
  }, [step]);

  const renderBody = () => {
    if (step === 'enterCode') {
      return (
        <Box
          ref={otpDialogRef}
          tabIndex={-1}
          aria-labelledby="otp-dialog-title"
          aria-describedby="otp-dialog-desc"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Typography
            id="otp-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize:
                isMobile || isLargeMob ? '18px' : isTablet ? '20px' : '28px',
              lineHeight: isMobile ? '24px' : isTablet ? '28px' : '36px',
              mb: '1.5rem',
              color: isError ? '#851109' : '#000'
            }}
            textAlign={isMobile ? 'center' : 'left'}
          >
            <img
              src={isError ? redLockIcon : lockIcon}
              alt={isError ? 'red invalid icon' : 'lock icon'}
              style={{ width: '18px', height: '18px' }}
            />{' '}
            {isError ? data.enterCode.invalidCodeTitle : data.enterCode.title}
          </Typography>
          <Typography
            id="otp-dialog-desc"
            sx={{
              fontSize: '1rem',
              lineHeight: '1.375rem',
              textAlign: 'center'
            }}
          >
            {isError ? (
              <>{data.enterCode.invalidCodeMessage} </>
            ) : (
              <>
                {data.enterCode.codeSentMessage}
                <strong>{email}</strong>.
              </>
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: '1.375rem',
              textAlign: 'center'
            }}
          >
            {isError ? (
              <>
                {data.enterCode.invalidCodeRetry}
                <button
                  style={{
                    color: '#1460AA',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0px',
                    padding: '0px',
                    marginTop: '-4px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setOtp(Array(5).fill(''));
                    setIsError(false);
                    setStep('enterCode');
                    dispatch(checkEmailRequest(email));
                  }}
                >
                  {data.enterCode.request}
                </button>
                .
              </>
            ) : (
              <>
                {data.enterCode.codeNotReceived}
                <button
                  style={{
                    color: '#1460AA',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0px',
                    padding: '0px',
                    marginTop: '-4px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setOtp(Array(5).fill(''));
                    setIsError(false);
                    setStep('enterCode');
                    dispatch(checkEmailRequest(email));
                  }}
                >
                  {data.enterCode.request}
                </button>
                .
              </>
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.125rem',
              lineHeight: '1.5rem',
              fontWeight: 600,
              textAlign: 'center',
              mt: '2.5rem'
            }}
          >
            {data.enterCode.enterCodePrompt}
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            mt={'1rem'}
            onPaste={handlePaste}
          >
            {otp.map((value, index) => (
              <OtpInput
                key={index}
                isError={isError}
                variant="outlined"
                value={value}
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
                onFocus={handleFocus}
                inputProps={{
                  maxLength: 1,
                  'aria-label': `OTP digit ${index + 1}`,
                  id: `otp-digit-${index + 1}`
                  // tabIndex: index === 0 ? 0 : -1
                }}
                inputRef={(el) => (inputRefs.current[index] = el)}
                error={isError}
              />
            ))}
          </Box>
          <span
            id="email-validity-announcement"
            tabIndex={isButtonDisabled ? 0 : -1}
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              margin: '-1px',
              padding: 0,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              border: 0
            }}
            aria-hidden={!isButtonDisabled}
          >
            {data.enterCode.ariaDisabledButton}
          </span>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            sx={{
              mt: '2.5rem',
              mb: '4.5rem'
            }}
          >
            {data.enterCode.continueButton}
          </Button>
        </Box>
      );
    } else if (step === 'verificationSuccess') {
      return (
        <Box
          sx={{
            pt: '3.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? '20px' : '28px',
              mb: '1.5rem',
              color: '#20842F',
              lineHeight: '2rem'
            }}
          >
            <img
              src={greenLockIcon}
              alt="green lock icon"
              style={{ width: '18px', height: '18px' }}
            />{' '}
            {data.verificationSuccess.title}
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: '1.375rem',
              fontWeight: 400,
              textAlign: 'center',
              mb: '2.5rem'
            }}
          >
            {data.verificationSuccess.message}
          </Typography>
          <CustomButton
            variant="contained"
            color="primary"
            onClick={() => setStep('managePreferences')}
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '24px',
              width: 'fit-content',
              height: '44px',
              backgroundColor: '#1460AA',
              textTransform: 'none',
              lineHeight: '20px',
              mb: '3.9rem',
              ':focus-visible': {
                outline: '2px solid black',
                outlineOffset: '2px'
              }
            }}
          >
            {data.verificationSuccess.managePreferencesButton}
          </CustomButton>
        </Box>
      );
    } else if (step === 'managePreferences') {
      return (
        <>
          <Box
            sx={{
              p: isLargeMob || isMobile ? 0 : '2.5rem',
              pr: isLargeMob || isMobile ? 0 : '3.5rem'
            }}
          >
            {' '}
            <Grid container spacing={'1.5rem'}>
              <Grid
                item
                xs={12}
                sm={isLargeMob ? 12 : 6}
                display="flex"
                alignItems="center"
              >
                <img
                  src={emailSendIcon}
                  alt="email send icon"
                  aria-hidden="true"
                  style={{ width: '24px', height: '24px', marginRight: '1rem' }}
                />
                <Box height={0} width={0} sx={{ color: 'transparent' }}>
                  {data.managePreferences.emailLabel}: {email}{' '}
                </Box>
                <Box aria-hidden="true">
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '.75rem',
                      lineHeight: '1rem',
                      width: 'inherit',
                      wordBreak: 'break-all',
                      color: '#717173'
                    }}
                  >
                    {data.managePreferences.emailLabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                      color: '#000',
                      width: 'inherit',
                      wordBreak: 'break-all'
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={isLargeMob ? 12 : 6}>
                <FormControl fullWidth variant="standard">
                  <InputLabel id="language-select-label">
                    {' '}
                    {data.managePreferences.preferredLanguageLabel}
                  </InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fill: '#1460AA',
                        width: '1.5rem !important'
                      }
                    }}
                    inputProps={{ 'aria-label': 'Preferred language' }}
                  >
                    {menuOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  component="h2"
                  sx={{ fontSize: '1rem', fontWeight: 400, pt: '1rem' }}
                >
                  {data.managePreferences.notificationDescription}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={isLargeMob ? 12 : 6}>
                <Typography
                  component="h3"
                  sx={{ fontSize: '1.125rem', fontWeight: 700, mb: '1rem' }}
                >
                  {data.managePreferences.updatesTitle}
                </Typography>
                <FormGroup>
                  {[
                    ...(preferenceArray?.filter(
                      (item) => item.preferences_type_id === 'PT001'
                    ) || []),
                    ...(notificationArray?.filter(
                      (item) => item.notification_type_id === 'NT001'
                    ) || []),
                    ...(preferenceArray?.filter(
                      (item) => item.preferences_type_id === 'PT002'
                    ) || [])
                  ].map((item) => (
                    <FormControlLabel
                      key={
                        item.preferences_type_id || item.notification_type_id
                      }
                      sx={{ fontSize: '1rem', fontWeight: 600 }}
                      control={
                        <Checkbox
                          checked={
                            preferences[
                              item.preferences_type_id ||
                                item.notification_type_id ||
                                false
                            ]
                          }
                          onChange={handlePreferenceChange}
                          name={
                            item.preferences_type_id ||
                            item.notification_type_id
                          }
                          sx={{
                            color: '#1460AA'
                          }}
                        />
                      }
                      label={
                        i18n.language === 'fr'
                          ? item.preferences_type_fr ||
                            item.notification_type_fr
                          : item.preferences_type_en ||
                            item.notification_type_en
                      }
                    />
                  ))}
                  {/* <FormControlLabel
                                        sx={{ fontSize: '1rem', fontWeight: 600 }}
                                        control={<Checkbox 
                                            checked={preferences.ndcProgramUpdates}
                                             onChange={handlePreferenceChange}
                                              name="ndcProgramUpdates" />}
                                        label={
                                           data.managePreferences.ndcProgramUpdates
                                            }
                                    />
                                    <FormControlLabel
                                        sx={{ fontSize: '1rem', fontWeight: 600 }}
                                        control={<Checkbox checked={preferences.releaseNotes}
                                         onChange={handlePreferenceChange} 
                                         name="releaseNotes" />}
                                        label={data.managePreferences.releaseNotes}
                                    />
                                    <FormControlLabel
                                        sx={{ fontSize: '1rem', fontWeight: 600 }}
                                        control={<Checkbox checked={
                                           preferences.surveysEvents}
                                         onChange={handlePreferenceChange}
                                          name="surveysEvents" />}
                                        label={ data.managePreferences.surveysEvents}
                                    /> */}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={isLargeMob ? 12 : 6} pb={'24px'}>
                <Typography
                  component="h3"
                  sx={{ fontSize: '1.125rem', fontWeight: 700, mb: '1rem' }}
                >
                  {data.managePreferences.alertsTitle}
                </Typography>
                <FormGroup>
                  {[
                    ...(notificationArray?.filter(
                      (item) => item.notification_type_id === 'NT003'
                    ) || [])
                  ].map((item) => (
                    <FormControlLabel
                      key={item.notification_type_id}
                      sx={{ fontSize: '1rem', fontWeight: 600, mb: '1rem' }}
                      control={
                        <Checkbox
                          checked={
                            preferences[item.notification_type_id || false]
                          }
                          onChange={handlePreferenceChange}
                          name={item.notification_type_id}
                          sx={{
                            color: '#1460AA'
                          }}
                        />
                      }
                      label={
                        i18n.language === 'fr'
                          ? item.notification_type_fr
                          : item.notification_type_en
                      }
                    />
                  ))}
                  {[
                    ...(notificationArray?.filter(
                      (item) => item.notification_type_id === 'NT002'
                    ) || [])
                  ].map((item) => (
                    <FormControlLabel
                      key={item.notification_type_id}
                      sx={{ fontSize: '1rem', fontWeight: 600 }}
                      control={
                        <Checkbox
                          checked={
                            preferences[item.notification_type_id || false]
                          }
                          onChange={handlePreferenceChange}
                          name={item.notification_type_id}
                          sx={{
                            color: '#1460AA'
                          }}
                        />
                      }
                      label={
                        i18n.language === 'fr'
                          ? item.notification_type_fr
                          : item.notification_type_en
                      }
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
          <Divider
            aria-hidden="true"
            sx={{
              borderColor: '#C9CACC',
              mx: isMobile ? '-1.5rem' : '-2.5rem'
            }}
          />
          <Box
            sx={{
              p: isLargeMob || isMobile ? 0 : '2.5rem',
              pr: isLargeMob || isMobile ? 0 : '3.5rem',
              pt: '1.7rem'
            }}
          >
            {' '}
            <Grid container spacing={'1.5rem'}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      name="agreeTerms"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '.75rem',
                        lineHeight: '1rem'
                      }}
                    >
                      {data.managePreferences.termsAndConditions}
                      <a
                        href={data.managePreferences.termsAndConditionsUrl}
                        style={{ color: '#000' }}
                      >
                        {data.managePreferences.termsAndConditions1}
                      </a>{' '}
                      {data.managePreferences.and}
                      <a
                        href={data.managePreferences.privacyPolicyUrl}
                        style={{ color: '#000' }}
                      >
                        {data.managePreferences.termsAndConditions2}
                      </a>
                      {data.managePreferences.termsAndConditions3}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={handleSavePreferences}
              type="submit"
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '24px',
                textTransform: 'none',
                lineHeight: '20px',
                marginTop: '2rem',
                ':focus-visible': {
                  outline: '2px solid black',
                  outlineOffset: '2px'
                }
              }}
            >
              {data.managePreferences.savePreferencesButton}
            </CustomButton>
          </Box>
        </>
      );
    } else if (step === 'preferencesSaved') {
      return (
        <Box
          sx={{
            p: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          <Snackbar
            open={snackbarOpen}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{
              position: 'absolute',
              top: '2.5rem !important',
              left: '2.5rem !important'
            }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={'success'}
              action={<></>}
              sx={{
                width: '26rem',
                borderRadius: '1rem',
                border: '0.5px solid #224D22',
                fontSize: '.875rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                bgcolor: '#439943',
                color: '#FFFFFF'
              }}
              icon={<CheckCircleIcon htmlColor="#FFFFFF" />}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: '1.375rem',
              fontWeight: 400,
              mb: '2.5rem',
              mt: '6rem'
            }}
          >
            {data.preferencesSaved.successMessage}"
            <a
              href={`/${i18n.language}/notifications-preferences`}
              style={{ color: '#000' }}
            >
              {data.preferencesSaved.successMessage1}
            </a>
            "{data.preferencesSaved.successMessage2}
          </Typography>

          <CustomButton
            variant="contained"
            color="primary"
            onClick={() => {
              navigate(`/${i18n.language}/`);
              handleClose && handleClose();
            }}
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '24px',
              width: 'fit-content',
              height: '44px',
              backgroundColor: '#1460AA',
              textTransform: 'none',
              lineHeight: '20px',
              alignSelf: 'start'
            }}
          >
            {data.preferencesSaved.continueBrowsingButton}
          </CustomButton>
        </Box>
      );
    }
  };

  return renderBody() as React.ReactElement;
};

export default OtpOptions;
